import {
  getLeoUuid,
  LeoUUID,
  getOptionalLeoUuid,
  getList,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { CustomPlaceholder, CustomPlaceholderDTO } from "./customPlaceholder";
/* eslint-disable import/export */
export abstract class GetCustomPlaceholdersRPC implements
  LeoRPC<GetCustomPlaceholdersRPC.Request, GetCustomPlaceholdersRPC.Response, GetCustomPlaceholdersRPC.Errors.Errors>
{
  abstract execute(
    request: GetCustomPlaceholdersRPC.Request
  ): Promise<LeoRPCResult<GetCustomPlaceholdersRPC.Response, GetCustomPlaceholdersRPC.Errors.Errors>>;
}

export namespace GetCustomPlaceholdersRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId?: string | null;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetCustomPlaceholdersRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID | null;
  
  
    constructor(
    projectId: LeoUUID | null = null
    ) {
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getOptionalLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
      };
      if (this.projectId !== undefined && this.projectId !== null){
        dto.projectId = this.projectId.toDTO();
      }
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetCustomPlaceholdersRPC(projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    customPlaceholders: CustomPlaceholderDTO[];
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetCustomPlaceholdersResponse: boolean = true;
  
    readonly customPlaceholders: CustomPlaceholder[];
  
  
    constructor(
    customPlaceholders: CustomPlaceholder[]
    ) {
      this.customPlaceholders = customPlaceholders;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const customPlaceholders = getList<CustomPlaceholderDTO, CustomPlaceholder>(
        dto,
        "customPlaceholders",
        CustomPlaceholder.fromDTO,
        InvalidResponseCustomPlaceholdersError
      );
      return new Response(
        customPlaceholders
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          customPlaceholders: this.customPlaceholders.map((e: CustomPlaceholder) => { return e.toDTO() })
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetCustomPlaceholders(customPlaceholders='${this.customPlaceholders}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetCustomPlaceholdersRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `GetCustomPlaceholdersRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    

    export type Errors = InvalidProjectId | ProjectAlreadyArchived;
    export type ErrorsDTO = InvalidProjectIdDTO | ProjectAlreadyArchivedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends GetCustomPlaceholdersRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidResponseCustomPlaceholdersError extends GetCustomPlaceholdersRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseCustomPlaceholdersError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetCustomPlaceholdersRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends GetCustomPlaceholdersRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

